/**
 * VariablesEditor — row-by-row table editor (Story 7.15 UX revamp).
 *
 * Replaces the original JSON textarea (Story 7.7 tech debt) with a friendlier
 * table UI:
 *
 *   - **Posição** (read-only) — auto-derived from `index + 1`; maps to `{{n}}`
 *     in `contentPreview`.
 *   - **Nome interno** — defaults to `var_${position}`; operator may rename for
 *     semantics (e.g. `customer_name`).
 *   - **Exemplo no preview** — value used by `TemplatePreview` to render the
 *     final message with real values. Never shipped to Meta.
 *
 * Optional integration via `contentPreview` prop:
 *   - Header coverage indicator: "✅ X de Y preenchidas" / "⚠️ X de Y".
 *   - Detects orphan `{{n}}` placeholders in the text and shows a one-click
 *     "+ Adicionar {{n}}" affordance.
 *   - Variables defined but absent from the text get a soft "não usada" badge.
 *
 * Wire shape passed to parent stays unchanged: `Array<{ name, position, example? }>`.
 * The form's submit path is untouched, so the backend contract holds.
 */

import { useMemo } from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface TemplateVariable {
  name: string;
  position: number;
  example?: string;
}

interface Props {
  value: TemplateVariable[];
  onChange: (next: TemplateVariable[]) => void;
  /**
   * Optional. When provided, the editor compares variables against `{{n}}`
   * placeholders in this text and shows coverage + orphan affordances.
   */
  contentPreview?: string | null;
  disabled?: boolean;
  id?: string;
}

const PLACEHOLDER_RE = /\{\{(\d+)\}\}/g;

function extractPositions(text: string | null | undefined): number[] {
  if (!text) return [];
  const set = new Set<number>();
  for (const m of text.matchAll(PLACEHOLDER_RE)) {
    const n = Number(m[1]);
    if (Number.isInteger(n) && n >= 1) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

function defaultName(position: number): string {
  return `var_${position}`;
}

function nextSequentialPosition(rows: TemplateVariable[]): number {
  if (rows.length === 0) return 1;
  return Math.max(...rows.map((r) => r.position)) + 1;
}

export function VariablesEditor({ value, onChange, contentPreview, disabled, id }: Props) {
  const placeholderPositions = useMemo(() => extractPositions(contentPreview), [contentPreview]);
  const definedPositions = useMemo(() => new Set(value.map((v) => v.position)), [value]);

  const orphanPositions = placeholderPositions.filter((p) => !definedPositions.has(p));
  const usedPositions = new Set(placeholderPositions);

  // Coverage counts only variables whose position is referenced in the text —
  // orphan variables (defined but absent from the text) are surfaced via the
  // "não usada" badge, not by inflating the filled-count denominator.
  const filledCount = value.filter(
    (v) => usedPositions.has(v.position) && (v.example?.length ?? 0) > 0,
  ).length;
  const totalReferenced = placeholderPositions.length;

  function handleAddRow(forcePosition?: number) {
    // Preserve `position` exactly as requested — never renumber. Renumbering
    // breaks the `{{n}}` ↔ variable contract when the text has gaps (e.g.
    // `{{1}}, {{3}}` without `{{2}}`), which Meta allows and the WABA sync
    // inbound can produce. The TemplatePreview already resolves variables
    // by position lookup (not by array index), so gaps are first-class.
    const position = forcePosition ?? nextSequentialPosition(value);
    const next = [...value, { name: defaultName(position), position, example: '' }].sort(
      (a, b) => a.position - b.position,
    );
    onChange(next);
  }

  function handleRemoveRow(index: number) {
    // Do NOT renumber remaining rows. Renumbering would silently re-map other
    // `{{n}}` placeholders in the text to different rows — e.g. removing the
    // row for `{{2}}` would make the old `{{3}}` row claim `{{2}}` while the
    // text still says `{{3}}`. Leave gaps so the orphan banner/badge can
    // surface the inconsistency explicitly.
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  }

  function handleFieldChange(index: number, patch: Partial<TemplateVariable>) {
    const next = value.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  }

  const showCoverage = (contentPreview ?? '').trim().length > 0;
  const coverageOk = totalReferenced > 0 && filledCount === totalReferenced;

  return (
    <div className="space-y-2" id={id}>
      <div className="flex items-center justify-between">
        <Label>Variáveis</Label>
        {showCoverage && totalReferenced > 0 ? (
          <span
            data-testid="variables-coverage"
            className={`text-xs ${coverageOk ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}
          >
            {coverageOk ? '✅' : '⚠️'} {filledCount} de {totalReferenced} preenchidas
          </span>
        ) : null}
      </div>

      {orphanPositions.length > 0 ? (
        <div
          data-testid="orphan-banner"
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-50 px-3 py-2 text-xs dark:border-amber-500/40 dark:bg-amber-950/30"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400"
            aria-hidden
          />
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <span className="text-amber-900 dark:text-amber-200">
              {orphanPositions.length === 1
                ? `Detectei {{${orphanPositions[0]}}} no texto, mas não há variável definida.`
                : `Detectei ${orphanPositions.length} placeholders no texto sem variáveis definidas (${orphanPositions.map((p) => `{{${p}}}`).join(', ')}).`}
            </span>
            {orphanPositions.map((pos) => (
              <Button
                key={pos}
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() => handleAddRow(pos)}
                aria-label={`Adicionar variável para {{${pos}}}`}
              >
                <Plus className="mr-1 h-3 w-3" aria-hidden />
                Adicionar {`{{${pos}}}`}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {value.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
          Nenhuma variável definida.
          {placeholderPositions.length === 0 ? (
            <>
              {' '}
              Use {`{{1}}`}, {`{{2}}`}... no texto para criar variáveis.
            </>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm" role="table" aria-label="Variáveis do template">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  Posição
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Nome interno
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Exemplo no preview
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {value.map((row, index) => {
                const isOrphan = showCoverage && !usedPositions.has(row.position);
                return (
                  <tr key={`${row.position}-${index}`} className="border-b last:border-b-0">
                    <td className="px-3 py-2 align-middle font-mono text-xs">
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">
                        {`{{${row.position}}}`}
                      </span>
                      {isOrphan ? (
                        <span
                          data-testid={`orphan-badge-${row.position}`}
                          className="ml-1 text-amber-700 dark:text-amber-400"
                          title="Não aparece no texto atual"
                          aria-label="Variável não usada no texto"
                        >
                          ⚠️
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <Input
                        aria-label={`Nome interno da variável ${row.position}`}
                        value={row.name}
                        onChange={(e) => handleFieldChange(index, { name: e.target.value })}
                        disabled={disabled}
                        placeholder={defaultName(row.position)}
                        className="h-8"
                      />
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <Input
                        aria-label={`Exemplo da variável ${row.position}`}
                        value={row.example ?? ''}
                        onChange={(e) => handleFieldChange(index, { example: e.target.value })}
                        disabled={disabled}
                        placeholder="ex.: João"
                        className="h-8"
                      />
                    </td>
                    <td className="px-3 py-2 text-right align-middle">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => handleRemoveRow(index)}
                        aria-label={`Remover variável ${row.position}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Para cada {`{{n}}`} no texto, defina um exemplo de teste (usado no preview).
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => handleAddRow()}
        >
          <Plus className="mr-1 h-3 w-3" aria-hidden />
          Adicionar variável
        </Button>
      </div>
    </div>
  );
}
